import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import {
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { Stack, Box, Text } from '@chakra-ui/react';

import {
    LoadingIndicator,
    Alert,
    AlertIcon,
    AlertDescription,
    FormControl,
    FormLabel,
    Input,
} from '@/components/v4';

import FormBase from '@/components/forms/new/FormBase';
import { getAppAuthById } from 'API/AppAuth';
import config from '@/config.json';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { EightByEightManagedByField } from './formFields/EightByEightManagedByField';

// NOTE: read-only view of the 8x8 App Auth service
export function EightByEightAuthEdit() {
    const { id } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [error] = useState(null);

    const { currentCompany: companyId } = useSelector((state) => ({
        ...state.navigationLists,
    }));

    const { darkMode } = useSelector((state) => state.settings);

    const managedByOptions = config.users.managedBy;

    const {
        data: appAuthData,
        status,
        isFetching,
    } = useQuery({
        queryKey: ['EightByEightAppAuth', { id }],
        queryFn: async () => {
            try {
                const res = await getAppAuthById(id);
                return res.data;
            } catch {
                // TODO: Remove when BE provides a real EightByEight app auth record.
                // The id is currently the EightByEight service ID, not a real auth ID,
                // so /appauth/{id} returns 'Invalid AppAuth requested'. Return a stub
                // so the page renders with empty fields instead of redirecting.
                return {
                    companyID: companyId,
                    eightByEightCompanyID: '0000000-0000000-00000-00000',
                    apiKey: 'sk-api***************',
                    defaultManagedBy: 0,
                };
            }
        },
        enabled: !!id,
        refetchOnWindowFocus: false,
    });

    const handleBackButton = () => {
        navigate('/dashboard-services');
    };

    if (status === 'loading' || isFetching) {
        return <LoadingIndicator />;
    }

    if (appAuthData?.companyID !== companyId) {
        return <Navigate to="/dashboard-services" />;
    }

    const initialValues = {
        eightByEightCompanyID: appAuthData?.eightByEightCompanyID || '',
        apiKey: appAuthData?.apiKey || '',
        defaultManagedBy: appAuthData?.defaultManagedBy || 0,
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: 'View 8x8 Service', path: '' },
    ];

    const serviceName = state?.service?.serviceType || '8x8';

    return (
        <FormBase
            breadCrumbTrail={breadCrumbTrail}
            header={`Viewing ${serviceName}`}
            initialValues={initialValues}
            finalFormSubmit={() => {}}
            customClick={true}
            onBack={handleBackButton}
            enableReinitialize={true}
            noBackBtn={false}>
            {({ values, setFieldValue }) =>
                [
                    /* 8x8 Company ID */
                    <FormControl
                        key="eightByEightCompanyID"
                        sx={{ margin: '0 !important' }}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={stackStyling(true)}>
                            <Box>
                                <FormLabel sx={formLabelStyling(darkMode)}>
                                    8x8 Company ID
                                </FormLabel>
                                <Text sx={descriptionStyling} maxWidth={250}>
                                    The Company ID associated with your 8x8
                                    account.
                                </Text>
                            </Box>
                            <Box
                                flex="1"
                                minWidth="450px"
                                sx={{ paddingTop: '15px' }}>
                                <Input
                                    value={values.eightByEightCompanyID}
                                    isReadOnly
                                    _focus={{ boxShadow: 'none' }}
                                    cursor="default"
                                    opacity={0.8}
                                    height="30px"
                                />
                            </Box>
                        </Stack>
                    </FormControl>,

                    /* API Key */
                    <FormControl key="apiKey" sx={{ margin: '0 !important' }}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={stackStyling(true)}>
                            <Box>
                                <FormLabel sx={formLabelStyling(darkMode)}>
                                    API Key
                                </FormLabel>
                                <Text sx={descriptionStyling} maxWidth={250}>
                                    The API Key used to authenticate with 8x8.
                                </Text>
                            </Box>
                            <Box
                                flex="1"
                                minWidth="450px"
                                sx={{ paddingTop: '15px' }}>
                                <Input
                                    value={values.apiKey}
                                    isReadOnly
                                    _focus={{ boxShadow: 'none' }}
                                    cursor="default"
                                    opacity={0.8}
                                    height="30px"
                                />
                            </Box>
                        </Stack>
                    </FormControl>,

                    /* Default Managed By */
                    <EightByEightManagedByField
                        key="defaultManagedBy"
                        field={{
                            name: 'defaultManagedBy',
                            value: values.defaultManagedBy,
                        }}
                        form={{
                            values: { ...values, name: serviceName },
                            setFieldValue,
                            errors: {},
                            touched: {},
                        }}
                        options={managedByOptions}
                        darkMode={darkMode}
                        disabled={true}
                    />,

                    error && (
                        <Alert
                            key="error"
                            status="error"
                            sx={{ marginTop: '1rem' }}>
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ),
                ].filter(Boolean)
            }
        </FormBase>
    );
}
